import apiClient from './client';

const handleProgress = (onProgress) => (progressEvent) => {
  if (!onProgress) return;
  const { loaded, total } = progressEvent;
  if (typeof total === 'number' && total > 0) {
    const percentCompleted = Math.round((loaded * 100) / total);
    onProgress(percentCompleted);
  } else {
    onProgress(null);
  }
};

// Allowed MIME types for each media category (matching backend configuration)
export const ALLOWED_MEDIA_TYPES = {
  image: ['image/jpeg', 'image/png'],
  document: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/plain',
  ],
  audio: ['audio/aac', 'audio/amr', 'audio/mpeg', 'audio/mp4', 'audio/ogg'],
  video: ['video/3gpp', 'video/mp4'],
};

// File size limits in bytes (matching backend configuration)
export const FILE_SIZE_LIMITS = {
  image: 5 * 1024 * 1024, // 5MB
  document: 100 * 1024 * 1024, // 100MB
  audio: 16 * 1024 * 1024, // 16MB
  video: 16 * 1024 * 1024, // 16MB
  default: 25 * 1024 * 1024, // 25MB
};

// Accept strings for file input elements
export const FILE_ACCEPT_STRINGS = {
  image: 'image/jpeg,image/png',
  document: '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt',
  audio: 'audio/aac,audio/amr,audio/mpeg,audio/mp4,audio/ogg,.aac,.amr,.mp3,.m4a,.ogg',
  video: 'video/3gpp,video/mp4,.3gp,.mp4',
  all: 'image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt',
};

/**
 * Detect media category from MIME type
 * @param {string} mimeType - The MIME type of the file
 * @returns {'image'|'video'|'audio'|'document'|null} The media category or null if not allowed
 */
export const getMediaCategory = (mimeType) => {
  if (!mimeType) return null;
  const type = mimeType.toLowerCase();
  if (ALLOWED_MEDIA_TYPES.image.includes(type)) return 'image';
  if (ALLOWED_MEDIA_TYPES.video.includes(type)) return 'video';
  if (ALLOWED_MEDIA_TYPES.audio.includes(type)) return 'audio';
  if (ALLOWED_MEDIA_TYPES.document.includes(type)) return 'document';
  return null;
};

/**
 * Validate a file against allowed types and size limits
 * @param {File} file - The file to validate
 * @param {string} [category] - Optional specific category to validate against
 * @returns {{ valid: boolean, error?: string, category?: string }}
 */
export const validateMediaFile = (file, category = null) => {
  if (!file) {
    return { valid: false, error: 'No file provided' };
  }

  const detectedCategory = getMediaCategory(file.type);

  // If a specific category is required, check it matches
  if (category && category !== 'all') {
    if (!ALLOWED_MEDIA_TYPES[category]?.includes(file.type)) {
      return {
        valid: false,
        error: `Invalid file type for ${category}. Allowed: ${ALLOWED_MEDIA_TYPES[category]?.join(', ')}`,
      };
    }
  } else if (!detectedCategory) {
    return {
      valid: false,
      error: `File type not allowed: ${file.type}`,
    };
  }

  const effectiveCategory = category && category !== 'all' ? category : detectedCategory;
  const sizeLimit = FILE_SIZE_LIMITS[effectiveCategory] || FILE_SIZE_LIMITS.default;

  if (file.size > sizeLimit) {
    const limitMB = Math.round(sizeLimit / (1024 * 1024));
    return {
      valid: false,
      error: `File size exceeds ${limitMB}MB limit for ${effectiveCategory} files`,
    };
  }

  return { valid: true, category: effectiveCategory };
};

/**
 * Create FormData for file upload
 * @param {File} file - The file to upload
 * @param {string} [fieldName='file'] - The form field name
 * @returns {FormData}
 */
export const createMediaFormData = (file, fieldName = 'file') => {
  const formData = new FormData();
  formData.append(fieldName, file);
  return formData;
};

export const uploadApi = {
  /**
   * Upload any media file (image, video, audio, document)
   * Uses /api/upload/media endpoint that accepts all types
   * @param {File|FormData} fileOrFormData - File or FormData to upload
   * @param {Function} [onProgress] - Progress callback (0-100)
   * @returns {Promise<{success: boolean, handle: string}>}
   */
  uploadMedia: (fileOrFormData, onProgress) => {
    const formData = fileOrFormData instanceof FormData
      ? fileOrFormData
      : createMediaFormData(fileOrFormData);

    return apiClient.post('/api/upload/media', formData, {
      onUploadProgress: handleProgress(onProgress),
    });
  },

  /**
   * Upload image file
   * @param {File|FormData} fileOrFormData - File or FormData to upload
   * @param {Function} [onProgress] - Progress callback
   */
  uploadImage: (fileOrFormData, onProgress) => {
    const formData = fileOrFormData instanceof FormData
      ? fileOrFormData
      : createMediaFormData(fileOrFormData, 'image');

    return apiClient.post('/api/upload/image', formData, {
      onUploadProgress: handleProgress(onProgress),
    });
  },

  /**
   * Upload video file
   * @param {File|FormData} fileOrFormData - File or FormData to upload
   * @param {Function} [onProgress] - Progress callback
   */
  uploadVideo: (fileOrFormData, onProgress) => {
    const formData = fileOrFormData instanceof FormData
      ? fileOrFormData
      : createMediaFormData(fileOrFormData, 'video');

    return apiClient.post('/api/upload/video', formData, {
      onUploadProgress: handleProgress(onProgress),
    });
  },

  /**
   * Upload audio file
   * @param {File|FormData} fileOrFormData - File or FormData to upload
   * @param {Function} [onProgress] - Progress callback
   */
  uploadAudio: (fileOrFormData, onProgress) => {
    const formData = fileOrFormData instanceof FormData
      ? fileOrFormData
      : createMediaFormData(fileOrFormData, 'audio');

    return apiClient.post('/api/upload/audio', formData, {
      onUploadProgress: handleProgress(onProgress),
    });
  },

  /**
   * Upload document file (PDF, DOC, XLS, PPT, TXT)
   * @param {File|FormData} fileOrFormData - File or FormData to upload
   * @param {Function} [onProgress] - Progress callback
   */
  uploadDocument: (fileOrFormData, onProgress) => {
    const formData = fileOrFormData instanceof FormData
      ? fileOrFormData
      : createMediaFormData(fileOrFormData, 'document');

    return apiClient.post('/api/upload/document', formData, {
      onUploadProgress: handleProgress(onProgress),
    });
  },

  // Legacy method - kept for backwards compatibility
  uploadFile: (formData, onProgress) => apiClient.post('/api/upload', formData, {
    onUploadProgress: handleProgress(onProgress),
  }),

  // Alias for uploadMedia - used for template headers
  uploadTemplateMedia: (fileOrFormData, onProgress) => {
    let formData = fileOrFormData;
    if (!(fileOrFormData instanceof FormData)) {
      formData = createMediaFormData(fileOrFormData, 'media');
    } else if (!fileOrFormData.get('media') && fileOrFormData.get('file')) {
      formData = new FormData();
      fileOrFormData.forEach((value, key) => {
        if (key === 'file') {
          formData.append('media', value);
        } else {
          formData.append(key, value);
        }
      });
    }

    return apiClient.post('/api/upload/template-media', formData, {
      onUploadProgress: handleProgress(onProgress),
    });
  },

  // Upload media for bot builder and return public URL
  uploadBotMedia: (fileOrFormData, onProgress) => {
    const formData = fileOrFormData instanceof FormData
      ? fileOrFormData
      : createMediaFormData(fileOrFormData);

    return apiClient.post('/api/upload/bot-media', formData, {
      onUploadProgress: handleProgress(onProgress),
    });
  },

  getFile: (id) => apiClient.get(`/api/upload/${id}`, { responseType: 'blob' }),

  deleteFile: (id) => apiClient.delete(`/api/upload/${id}`),

  getSignedUrl: (filename, contentType) => apiClient.post('/api/upload/signed-url', { filename, contentType }),
};

export default uploadApi;
